// src/components/squads/SquadDetailModal.tsx
//
// Detalhe da Squad em MODAL sobre a lista — mesma técnica de Skills (§10.2):
// /squads/:id é deep-link, fechar volta pra /squads sem remontar a listagem.
//
// Quatro abas: Charter · Roster · Execuções · Versões.
//
// O ROSTER VEM DO SQUAD.md, não da tabela `squad_members`. Quem decide isso é o
// backend (`validateSquadMd` na rota) e o motivo é a regra do RAIOS: governança
// e estrutura vivem no MD, o banco guarda estado histórico de execução. Se a
// tabela divergir do MD, quem executa é o MD — e a tela precisa mostrar o time
// que de fato roda, não um espelho que pode ter envelhecido.
//
// A aba Execuções abre a TIMELINE da run (DOD-SQD-504): membros ativados e
// handoffs no mesmo eixo, cada um com seu estado terminal explícito.

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type SquadDetail,
  type SquadRun,
  type SquadRunDetail,
  type SquadVersion,
  type SquadMetricsResponse,
} from '@/lib/api';
import { listAgentsCached } from '@/lib/meta-cache';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs } from '@/components/ui/Tabs';
import { fmtDateTime, fmtMoneyUSD, fmtNumber } from '@/lib/format';
import {
  SCOPE_BADGE,
  STATUS_BADGE,
  STATUS_LABELS,
  RUN_STATUS_BADGE,
  RUN_STATUS_LABELS,
  HANDOFF_STATUS_BADGE,
  HANDOFF_STATUS_LABELS,
  MEMBER_STATUS_LABELS,
  TOPOLOGY_LABELS,
  fmtContagem,
  fmtDuracao,
  rotular,
} from './squad-constants';
import {
  Users,
  FileText,
  Activity,
  GitBranch,
  BarChart3,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Crown,
} from 'lucide-react';

type Aba = 'charter' | 'roster' | 'runs' | 'metrics' | 'versions';

const ABAS = [
  { value: 'charter', label: 'Charter', icon: FileText },
  { value: 'roster', label: 'Roster', icon: Users },
  { value: 'runs', label: 'Execuções', icon: Activity },
  { value: 'metrics', label: 'Métricas', icon: BarChart3 },
  { value: 'versions', label: 'Versões', icon: GitBranch },
];

/** Percentual a partir de fração 0-1. `null` vira "—", nunca "0%". */
function pct(v: number | null | undefined): string {
  return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
}

/** Segundos legíveis. `null` = ainda não terminou / não há amostra. */
function seg(v: number | null | undefined): string {
  if (v == null) return '—';
  const n = Number(v);
  if (n < 60) return `${n.toFixed(0)}s`;
  const m = Math.floor(n / 60);
  return m < 60 ? `${m}min ${(n % 60).toFixed(0)}s` : `${Math.floor(m / 60)}h ${m % 60}min`;
}

interface SquadDetailModalProps {
  squadId: string;
  open: boolean;
  onClose: () => void;
}

export function SquadDetailModal({ squadId, open, onClose }: SquadDetailModalProps) {
  const [squad, setSquad] = useState<SquadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aba, setAba] = useState<Aba>('charter');

  // ── Nome HUMANO dos agentes (pedido direto do dono, 2026-08-04) ────────────
  // O payload da squad só traz agent_slug (roster do SQUAD.md e membros da
  // run); a tela mostrava "orchestrator sm" — papel + sigla. Resolvemos
  // slug→nome via listAgents (cache 60s compartilhado) e mostramos o NOME,
  // com papel/slug como detalhe. Falha na carga degrada pro slug (nunca
  // esconde o membro). Pendência backend: resolver agent_name server-side.
  const [nomesPorSlug, setNomesPorSlug] = useState<Map<string, string> | null>(null);
  useEffect(() => {
    let cancelado = false;
    listAgentsCached()
      .then((agents) => {
        if (!cancelado) setNomesPorSlug(new Map(agents.map((a) => [a.slug, a.name])));
      })
      .catch(() => {
        /* degrade: slugs continuam na tela */
      });
    return () => {
      cancelado = true;
    };
  }, []);
  const nomeDoAgente = useCallback(
    (slug: string | null | undefined): string | null =>
      (slug ? (nomesPorSlug?.get(slug) ?? null) : null),
    [nomesPorSlug],
  );

  const [runs, setRuns] = useState<SquadRun[] | null>(null);
  const [runsErro, setRunsErro] = useState('');
  const [versoes, setVersoes] = useState<SquadVersion[] | null>(null);
  const [versoesErro, setVersoesErro] = useState('');
  const [metricas, setMetricas] = useState<SquadMetricsResponse | null>(null);
  const [metricasErro, setMetricasErro] = useState('');
  const [runAberta, setRunAberta] = useState<SquadRunDetail | null>(null);
  const [runCarregando, setRunCarregando] = useState(false);
  const [runErro, setRunErro] = useState('');

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setLoading(true);
      setError('');
      try {
        const d = await api.getSquad(squadId);
        if (!cancelado) setSquad(d);
      } catch (err) {
        if (!cancelado) setError(err instanceof Error ? err.message : 'Erro ao carregar squad');
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, [squadId]);

  // Execuções e versões carregam SOB DEMANDA — abrir o detalhe de uma squad não
  // precisa custar três requisições quando o usuário só quer ler o charter.
  useEffect(() => {
    if (aba !== 'runs' || runs !== null) return;
    let cancelado = false;
    api
      .listSquadRuns(squadId, 50)
      .then((r) => {
        if (!cancelado) setRuns(r);
      })
      .catch((err) => {
        // Erro de carga NÃO vira lista vazia: "esta squad nunca executou" é uma
        // afirmação, e não temos base pra fazê-la quando a query falhou.
        if (!cancelado) setRunsErro(err instanceof Error ? err.message : 'Erro ao carregar execuções');
      });
    return () => {
      cancelado = true;
    };
  }, [aba, squadId, runs]);

  useEffect(() => {
    if (aba !== 'metrics' || metricas !== null) return;
    let cancelado = false;
    api
      .getSquadMetrics(squadId)
      .then((m) => {
        if (!cancelado) setMetricas(m);
      })
      .catch((err) => {
        // Falha de carga NÃO vira "sem métricas": números ausentes por erro são
        // indistinguíveis de números ausentes por falta de execução, e a
        // segunda leitura é a confortável.
        if (!cancelado) setMetricasErro(err instanceof Error ? err.message : 'Erro ao carregar métricas');
      });
    return () => {
      cancelado = true;
    };
  }, [aba, squadId, metricas]);

  useEffect(() => {
    if (aba !== 'versions' || versoes !== null) return;
    let cancelado = false;
    api
      .listSquadVersions(squadId)
      .then((v) => {
        if (!cancelado) setVersoes(v);
      })
      .catch((err) => {
        if (!cancelado) setVersoesErro(err instanceof Error ? err.message : 'Erro ao carregar versões');
      });
    return () => {
      cancelado = true;
    };
  }, [aba, squadId, versoes]);

  const abrirRun = useCallback(async (runId: string) => {
    setRunCarregando(true);
    setRunErro('');
    try {
      setRunAberta(await api.getSquadRun(runId));
    } catch (err) {
      setRunErro(err instanceof Error ? err.message : 'Erro ao carregar a execução');
    } finally {
      setRunCarregando(false);
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="xl" className="max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-raios-border shrink-0">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Users className="w-5 h-5 text-raios-accent-text" aria-hidden />
            <span>{squad?.name ?? 'Squad'}</span>
            {squad && (
              <>
                <span className="text-xs font-mono font-normal text-raios-muted">{squad.slug}</span>
                {squad.current_version && (
                  <Badge variant="info" className="font-mono text-[10px]">
                    v{squad.current_version}
                  </Badge>
                )}
                <Badge variant={SCOPE_BADGE[squad.scope] ?? 'default'} className="text-[10px]">
                  {squad.scope}
                </Badge>
                <Badge variant={STATUS_BADGE[squad.status] ?? 'default'} className="text-[10px]">
                  {rotular(STATUS_LABELS, squad.status)}
                </Badge>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-64 rounded-lg" />
              <Skeleton className="h-48 rounded-xl" />
            </div>
          ) : error || !squad ? (
            <EmptyState
              icon={AlertCircle}
              title="Erro ao carregar squad"
              description={error || 'Squad não encontrada'}
            />
          ) : (
            <>
              <Tabs items={ABAS} value={aba} onChange={(v) => setAba(v as Aba)} className="mb-4" />

              {aba === 'charter' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Celula label="Slug" valor={squad.slug} mono />
                    <Celula label="Topologia" valor={rotular(TOPOLOGY_LABELS, squad.topology)} />
                    {/* NOME do agente orquestrador ("Sam"), não o slug/sigla
                        ("sm") — o papel já está no rótulo; slug vira detalhe. */}
                    <Celula
                      label="Orquestrador"
                      valor={nomeDoAgente(squad.orchestrator_slug) ?? squad.orchestrator_slug ?? '—'}
                      mono={!nomeDoAgente(squad.orchestrator_slug)}
                      detalhe={
                        nomeDoAgente(squad.orchestrator_slug) ? squad.orchestrator_slug : null
                      }
                    />
                    <Celula label="Papéis" valor={fmtContagem(squad.roster?.length ?? null)} />
                  </div>
                  {squad.parent_global_id && (
                    <p className="text-xs text-raios-muted">
                      Instância criada a partir de uma squad GLOBAL
                      {squad.origin_version ? ` (origem v${squad.origin_version})` : ''}.
                    </p>
                  )}
                  <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary bg-raios-elevated/30 border border-raios-border rounded-md p-4 overflow-x-auto">
                    {squad.charter ||
                      '(charter não resolvido — SQUAD.md ausente ou ilegível no storage)'}
                  </pre>
                </div>
              )}

              {aba === 'roster' && (
                squad.roster && squad.roster.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-raios-muted">
                      Papéis lidos do SQUAD.md — a mesma fonte que o runtime executa.
                    </p>
                    {squad.roster.map((m, i) => (
                      <div
                        key={`${m.role}-${i}`}
                        className="flex items-start gap-3 border border-raios-border rounded-md p-3 bg-raios-elevated/20"
                      >
                        <div className="mt-0.5">
                          {m.agent_slug && m.agent_slug === squad.orchestrator_slug ? (
                            <Crown className="w-4 h-4 text-raios-warning-text" aria-label="orquestrador" />
                          ) : (
                            <Users className="w-4 h-4 text-raios-muted" aria-hidden />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* NOME do agente em destaque; papel pequeno abaixo
                              (regra do dono — não "orchestrator sm"). Sem nome
                              resolvido, o slug segura a linha; sem agente, o
                              papel + aviso honesto. */}
                          <p className="text-sm font-semibold text-raios-text truncate">
                            {nomeDoAgente(m.agent_slug) ?? m.agent_slug ?? m.role}
                          </p>
                          <p className="text-xs text-raios-muted truncate">
                            {m.role}
                            {m.agent_slug
                              ? <> · <span className="font-mono">{m.agent_slug}</span></>
                              : ' · agente não vinculado'}
                          </p>
                          <div className="flex gap-2 flex-wrap mt-1.5">
                            {m.execution_role && (
                              <Badge variant="default" className="text-[10px]">{m.execution_role}</Badge>
                            )}
                            {m.activation && (
                              <Badge variant="info" className="text-[10px]">{m.activation}</Badge>
                            )}
                            {Array.isArray(m.tools) && m.tools.length > 0 && (
                              <Badge variant="default" className="text-[10px]">
                                {m.tools.length} {m.tools.length === 1 ? 'tool' : 'tools'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={Users}
                    title="Roster indisponível"
                    description="O SQUAD.md não foi resolvido no storage, ou não declara papéis."
                  />
                )
              )}

              {aba === 'runs' && (
                runAberta || runCarregando || runErro ? (
                  <TimelineDaRun
                    run={runAberta}
                    carregando={runCarregando}
                    erro={runErro}
                    nomeDoAgente={nomeDoAgente}
                    onVoltar={() => {
                      setRunAberta(null);
                      setRunErro('');
                    }}
                  />
                ) : runsErro ? (
                  <EmptyState icon={AlertCircle} title="Erro ao carregar execuções" description={runsErro} />
                ) : runs === null ? (
                  <Skeleton className="h-40 rounded-xl" />
                ) : runs.length === 0 ? (
                  <EmptyState
                    icon={Activity}
                    title="Nenhuma execução"
                    description="Esta squad ainda não foi acionada por nenhuma demanda."
                  />
                ) : (
                  <div className="space-y-2">
                    {runs.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => abrirRun(r.id)}
                        className="w-full text-left border border-raios-border rounded-md p-3 bg-raios-elevated/20 hover:border-raios-accent transition"
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge variant={RUN_STATUS_BADGE[r.status] ?? 'default'} className="text-[10px]">
                              {rotular(RUN_STATUS_LABELS, r.status)}
                            </Badge>
                            <span className="text-xs font-mono text-raios-muted truncate">
                              {r.id.slice(0, 8)}…
                            </span>
                          </div>
                          <span className="text-xs text-raios-muted">{fmtDateTime(r.started_at)}</span>
                        </div>
                        {r.status_reason && (
                          <p className="text-xs text-raios-text-secondary mt-1.5">{r.status_reason}</p>
                        )}
                        <div className="flex gap-4 flex-wrap text-2xs text-raios-muted mt-2">
                          <span>Duração <b>{fmtDuracao(r.started_at, r.completed_at)}</b></span>
                          <span>Iterações <b>{fmtContagem(r.iterations_used)}</b></span>
                          <span>Pico de membros <b>{fmtContagem(r.active_members_peak)}</b></span>
                          <span>Custo <b>{fmtMoneyUSD(r.cost_usd_total)}</b></span>
                        </div>
                      </button>
                    ))}
                  </div>
                )
              )}

              {aba === 'metrics' && (
                metricasErro ? (
                  <EmptyState icon={AlertCircle} title="Erro ao carregar métricas" description={metricasErro} />
                ) : metricas === null ? (
                  <Skeleton className="h-40 rounded-xl" />
                ) : metricas.metrics === null ? (
                  // `null` vem do backend quando a squad nunca executou. Mostrar
                  // uma grade de zeros afirmaria "acceptance 0%", que é
                  // diferente de "não há o que medir".
                  <EmptyState
                    icon={BarChart3}
                    title="Sem execuções para medir"
                    description="As métricas aparecem depois da primeira execução desta squad."
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Celula label="Outcome acceptance" valor={pct(metricas.metrics.outcome_acceptance)} />
                      <Celula label="First-pass yield" valor={pct(metricas.metrics.first_pass_yield)} />
                      <Celula label="Handoff rejection" valor={pct(metricas.metrics.handoff_rejection_rate)} />
                      <Celula
                        label="Custo / outcome aceito"
                        valor={metricas.metrics.custo_por_outcome_aceito_usd == null
                          ? '—'
                          : fmtMoneyUSD(metricas.metrics.custo_por_outcome_aceito_usd, 4)}
                      />
                      <Celula label="Cycle time p50" valor={seg(metricas.metrics.cycle_time_p50_s)} />
                      <Celula label="Cycle time p95" valor={seg(metricas.metrics.cycle_time_p95_s)} />
                      <Celula
                        label="Execuções"
                        valor={`${metricas.metrics.runs_aceitas}/${metricas.metrics.runs_terminais} aceitas`}
                      />
                      <Celula
                        label="Escaladas p/ humano"
                        valor={String(metricas.metrics.runs_escaladas)}
                      />
                    </div>

                    <p className="text-2xs text-raios-muted">
                      Taxas calculadas sobre as execuções <b>terminais</b> ({metricas.metrics.runs_terminais} de{' '}
                      {metricas.metrics.runs_total}). Execução em andamento ainda não passou nem falhou.
                    </p>

                    {/* DOD-SQD-405 — a reconciliação só aparece quando NÃO fecha.
                        Mostrar "0 divergências" a cada visita treina o operador a
                        ignorar o bloco, e aí ele deixa de ser visto quando importa. */}
                    {metricas.reconciliacao.runs_que_nao_fecham > 0 && (
                      <div className="border border-raios-warning/40 bg-raios-warning/5 rounded-md p-3">
                        <p className="text-xs font-semibold text-raios-warning-text mb-2">
                          {metricas.reconciliacao.runs_que_nao_fecham} de {metricas.reconciliacao.runs_avaliadas}{' '}
                          execuções com custo que não fecha
                        </p>
                        <p className="text-2xs text-raios-muted mb-2">
                          O total da run difere da soma dos membros — pode ser custo do orquestrador não
                          atribuído, ou dupla contagem em retry.
                        </p>
                        <div className="space-y-1">
                          {metricas.reconciliacao.amostra.slice(0, 5).map((r) => (
                            <div key={r.squad_run_id} className="flex justify-between text-2xs font-mono">
                              <span className="text-raios-muted">{r.squad_run_id.slice(0, 8)}… ({r.run_status})</span>
                              <span className="text-raios-text-secondary">
                                run {fmtMoneyUSD(r.custo_run, 4)} · membros {fmtMoneyUSD(r.custo_membros, 4)} ·{' '}
                                <b>Δ {fmtMoneyUSD(r.delta_custo, 4)}</b>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {aba === 'versions' && (
                versoesErro ? (
                  <EmptyState icon={AlertCircle} title="Erro ao carregar versões" description={versoesErro} />
                ) : versoes === null ? (
                  <Skeleton className="h-32 rounded-xl" />
                ) : versoes.length === 0 ? (
                  <EmptyState
                    icon={GitBranch}
                    title="Nenhuma versão publicada"
                    description="A squad ainda não tem uma versão registrada."
                  />
                ) : (
                  <div className="space-y-2">
                    {versoes.map((v) => (
                      <div
                        key={v.id}
                        className="flex items-center justify-between gap-3 flex-wrap border border-raios-border rounded-md p-3 bg-raios-elevated/20"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="info" className="font-mono text-[10px]">v{v.version}</Badge>
                          {/* Publicada ou não: o estado da versão É published_at.
                              Não existe coluna `status` em squad_versions. */}
                          <Badge variant={v.published_at ? 'success' : 'default'} className="text-[10px]">
                            {v.published_at ? 'publicada' : 'rascunho'}
                          </Badge>
                          {v.id === squad.current_version_id && (
                            <Badge variant="success" className="text-[10px]">corrente</Badge>
                          )}
                        </div>
                        <div className="flex gap-4 text-2xs text-raios-muted">
                          {v.content_hash && (
                            <span className="font-mono" title={v.content_hash}>
                              {v.content_hash.slice(0, 12)}…
                            </span>
                          )}
                          <span>{fmtDateTime(v.published_at ?? v.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Timeline de uma run (DOD-SQD-504): membros ativados e handoffs no mesmo eixo.
 *
 * Membro e handoff são coisas diferentes e aparecem diferentes — um é quem
 * trabalhou, o outro é a passagem de bastão. Colapsar os dois numa lista só
 * apagaria a informação que a aba existe para mostrar.
 */
function TimelineDaRun({
  run,
  carregando,
  erro,
  nomeDoAgente,
  onVoltar,
}: {
  run: SquadRunDetail | null;
  carregando: boolean;
  erro: string;
  /** slug → nome humano do agente (resolvido pela modal; null = sem nome). */
  nomeDoAgente: (slug: string | null | undefined) => string | null;
  onVoltar: () => void;
}) {
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onVoltar}
        className="flex items-center gap-1.5 text-xs text-raios-muted hover:text-raios-text"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Voltar às execuções
      </button>

      {carregando ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : erro || !run ? (
        <EmptyState icon={AlertCircle} title="Erro ao carregar a execução" description={erro} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Celula label="Estado" valor={rotular(RUN_STATUS_LABELS, run.status)} />
            <Celula label="Duração" valor={fmtDuracao(run.started_at, run.completed_at)} />
            <Celula label="Tokens" valor={fmtNumber((run.tokens_input_total ?? 0) + (run.tokens_output_total ?? 0))} />
            <Celula label="Custo" valor={fmtMoneyUSD(run.cost_usd_total)} />
          </div>

          {run.status_reason && (
            <p className="text-xs text-raios-text-secondary border-l-2 border-raios-border pl-3">
              {run.status_reason}
            </p>
          )}

          <section>
            <h3 className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-2">
              Membros ativados ({run.members.length})
            </h3>
            {run.members.length === 0 ? (
              <p className="text-xs text-raios-muted">Nenhum membro foi ativado nesta execução.</p>
            ) : (
              <div className="space-y-2">
                {run.members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-start justify-between gap-3 flex-wrap border border-raios-border rounded-md p-3 bg-raios-elevated/20"
                  >
                    <div className="min-w-0">
                      {/* NOME do agente em destaque; papel + slug pequenos
                          abaixo (mesma regra do roster). */}
                      <p className="text-sm font-semibold text-raios-text truncate">
                        {nomeDoAgente(m.agent_slug) ?? m.agent_slug ?? m.role}
                      </p>
                      <p className="text-xs text-raios-muted truncate">
                        {m.role}
                        {m.agent_slug && <> · <span className="font-mono">{m.agent_slug}</span></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {m.depth != null && m.depth > 0 && (
                        <Badge variant="default" className="text-[10px]">profundidade {m.depth}</Badge>
                      )}
                      <Badge
                        variant={m.status === 'completed' ? 'success' : m.status === 'failed' ? 'danger' : 'info'}
                        className="text-[10px]"
                      >
                        {rotular(MEMBER_STATUS_LABELS, m.status)}
                      </Badge>
                      <span className="text-2xs text-raios-muted">
                        {fmtDuracao(m.activated_at, m.completed_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-2">
              Handoffs ({run.handoffs.length})
            </h3>
            {run.handoffs.length === 0 ? (
              <p className="text-xs text-raios-muted">Nenhuma passagem de bastão nesta execução.</p>
            ) : (
              <div className="space-y-2">
                {run.handoffs.map((h) => (
                  <div key={h.id} className="border border-raios-border rounded-md p-3 bg-raios-elevated/20">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ArrowRight className="w-3.5 h-3.5 text-raios-muted" aria-hidden />
                      <Badge variant={HANDOFF_STATUS_BADGE[h.status] ?? 'default'} className="text-[10px]">
                        {rotular(HANDOFF_STATUS_LABELS, h.status)}
                      </Badge>
                      {h.mode && <Badge variant="default" className="text-[10px]">{h.mode}</Badge>}
                      <span className="text-2xs text-raios-muted ml-auto">{fmtDateTime(h.proposed_at)}</span>
                    </div>
                    {h.objective && (
                      <p className="text-xs text-raios-text-secondary mt-1.5">{h.objective}</p>
                    )}
                    {h.rejection_reason && (
                      <p className="text-2xs text-raios-muted mt-1">{h.rejection_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Celula({
  label,
  valor,
  mono,
  detalhe,
}: {
  label: string;
  valor: string;
  mono?: boolean;
  /** Linha pequena sob o valor (ex.: slug técnico sob o nome do agente). */
  detalhe?: string | null;
}) {
  return (
    <div className="bg-raios-elevated/30 border border-raios-border rounded-md p-3 min-w-0">
      <p className="text-2xs font-semibold uppercase tracking-widest text-raios-muted mb-1 truncate" title={label}>
        {label}
      </p>
      <p
        className={`text-sm font-semibold text-raios-text tabular-nums truncate ${mono ? 'font-mono' : ''}`}
        title={valor}
      >
        {valor}
      </p>
      {detalhe && (
        <p className="text-2xs font-mono text-raios-muted truncate" title={detalhe}>
          {detalhe}
        </p>
      )}
    </div>
  );
}
